import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ServerLinks } from '@shared/services/server-links';
import { ServerLinkName } from '@shared/models/server-links-model';
import { Observable, of } from 'rxjs';
import { Store } from '@ngxs/store';
import { SharedState } from '@shared/state/shared/shared.state';
import { map } from 'rxjs/operators';
import { VkService } from '@shared/services/vk.service';
import { SearchForCatalogsResponse } from '../../search/models';
import { AppStateService } from '../modules/apps/services/app-state.service';
import { GlobalSearchRow, GlobalSearchType } from '../models/global-search';
import { VersionForVisuallyImpairedRoutes } from '../models/routes';
import { ImpairedCatalogItemsRoutes } from '../modules/data/modules/models/routes';

@Injectable({
  providedIn: 'root'
})
export class GlobalSearchService {
  private _vkForumLinkWall: string;
  private _urlGlobalSearch = '';

  constructor(
    private http: HttpClient,
    serverLinks: ServerLinks,
    private appState: AppStateService,
    private store: Store,
    private vkService: VkService
  ) {
    this._urlGlobalSearch = serverLinks.getLink(ServerLinkName.GlobalSearch);
    this._vkForumLinkWall = serverLinks.getLink(ServerLinkName.VkGroupLinkWall);
  }

  public getSearchForCatalogs(searchString: string): Observable<GlobalSearchRow[]> {
    if (searchString.trim() === '') {
      return of([]);
    }
    return this.http
      .post<SearchForCatalogsResponse>(`${this._urlGlobalSearch}catalogs`, {
        fields: [{ searchType: 'name' }],
        fragmentSize: 20,
        idCategory: [],
        limit: 10,
        offset: 0,
        search: searchString
      })
      .pipe(
        map(res => {
          const catalogs = res.catalogs.map(c => ({
            name: `${c.name} (набор данных)`,
            link: `/visually-impaired/${VersionForVisuallyImpairedRoutes.DataSet}/${c.idDataset}/${ImpairedCatalogItemsRoutes.Table}`,
            type: 'Catalog' as GlobalSearchType,
            queryParams: {
              isDynamic: c.isDynamic
            }
          }));
          const apps = (this.appState?.allApp ?? [])
            .filter(x => x.name?.toLowerCase().includes(searchString?.toLowerCase()))
            .map(a => ({
              name: `${a.name} (приложения)`,
              link: `/visually-impaired/${VersionForVisuallyImpairedRoutes.Apps}/${a.id}`,
              type: 'App' as GlobalSearchType
            }));

          const categories = this.store
            .selectSnapshot(SharedState.getCategories)
            .filter(с => с?.fullName?.toLowerCase()?.includes(searchString.toLowerCase()))
            .map(a => ({
              name: `${a.fullName} (категории)`,
              data: a,
              link: `/visually-impaired/${VersionForVisuallyImpairedRoutes.DataSet}`,
              type: 'Category' as GlobalSearchType
            }));

          const providers = this.store
            .selectSnapshot(SharedState.getProviders)
            .filter(с => с?.fullNameOIV?.toLowerCase()?.includes(searchString.toLowerCase()))
            .map(a => ({
              name: `${a.fullNameOIV} (ОИВ)`,
              link: `/visually-impaired/${VersionForVisuallyImpairedRoutes.DataSet}`,
              data: a,
              type: 'Provider' as GlobalSearchType
            }));

          const vkNewsTitle = this.vkService.vkPosts$.value
            .filter(с => с?.title?.toLowerCase()?.includes(searchString.toLowerCase()))
            .map(news => ({
              name: `${news.title} (Новости)`,
              link: `${this._vkForumLinkWall}${news.postId}`,
              type: 'News' as GlobalSearchType
            }));

          const vkNewsText = this.vkService.vkPosts$.value
            .filter(с => с?.text?.toLowerCase()?.includes(searchString.toLowerCase()))
            .map(news => {
              const findStr = news.text.substring(news.text.toLowerCase().indexOf(searchString.toLowerCase()), searchString.length + 30);
              return {
                name: `${findStr} (Новости)`,
                link: `${this._vkForumLinkWall}${news.postId}`,
                type: 'News' as GlobalSearchType
              };
            });

          return [...catalogs, ...apps, ...categories, ...providers, ...vkNewsTitle, ...vkNewsText];
        })
      );
  }
}
